import React from 'react';
import { Navigate } from 'react-router-dom';
import { SkeletonText } from '@carbon/react';
import { NavCapability } from 'src/config/settingsNav';
import { useAuthPermissions } from 'src/hooks/useAuthPermissions';
import { useSettingsNav } from 'src/hooks/useSettingsNav';

interface RequireCapabilityProps {
  capability: NavCapability;
  /**
   * Where to send a viewer who lacks the capability. Defaults to the settings area's first
   * allowed path, which is where a settings page's viewer belongs — but a /storefront page
   * must pass its own, or an owner who can manage settings and a member who cannot would both
   * be bounced into Settings.
   */
  fallbackPath?: string;
  children: JSX.Element;
}

/** The shared area-nav skeleton, not a settings-only one: every gated area renders it while
 * permissions settle. */
export const SettingsSkeleton = () => (
  <div className={'settings-page settings-page--loading'} data-testid={'settings-skeleton'}>
    <div className={'settings-side-nav'}>
      <SkeletonText paragraph lineCount={8} />
    </div>
    <div className={'settings-page__body'}>
      <SkeletonText heading />
      <SkeletonText paragraph lineCount={4} />
    </div>
  </div>
);

const RequireCapability = (props: RequireCapabilityProps) => {
  const permissions = useAuthPermissions();
  const { firstAllowedPath } = useSettingsNav();

  if (!permissions.ready) {
    return <SettingsSkeleton />;
  }
  if (!permissions[props.capability]) {
    return <Navigate to={props.fallbackPath ?? firstAllowedPath} replace />;
  }
  return props.children;
};

export default RequireCapability;
